import { useState, useEffect } from 'react';
import { 
  FlaskConical, ArrowRight, ArrowLeft, Loader2, Target,
  Building2, Calendar, CheckCircle, Plus, Trash2, GripVertical
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Json } from '@/integrations/supabase/types';

interface CreateExperimentWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface Company {
  id: string;
  company_name: string;
}

interface SurveyQuestion {
  id: string;
  question: string;
  type: 'scale' | 'multiple_choice' | 'text';
  options?: string[];
  required: boolean;
}

interface ChallengeConfig {
  goal: string;
  metric: string;
  targetValue: number;
  durationDays: number;
}

interface LearningContent {
  points: string[];
  quizQuestion?: string;
  quizOptions?: string[];
  quizAnswer?: number;
}

const INTERVENTION_TYPES = [
  { id: 'survey', label: 'Survey', description: 'Collect research data', needsSetup: true },
  { id: 'micro_learning', label: 'Micro Learning', description: 'Educational content with quiz', needsSetup: true },
  { id: 'commitment', label: 'Commitment Request', description: 'Implementation intentions', needsSetup: false },
  { id: 'norm_nudge', label: 'Social Norm Nudge', description: 'Peer comparison messaging', needsSetup: false },
  { id: 'recognition', label: 'Recognition', description: 'Achievement celebration', needsSetup: false },
  { id: 'challenge', label: 'Challenge', description: 'Behavior change challenge', needsSetup: true },
];

export default function CreateExperimentWizard({ onComplete, onCancel }: CreateExperimentWizardProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Hypothesis
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [expectedEffectSize, setExpectedEffectSize] = useState(0.3);

  // Step 2: Intervention design
  const [interventionType, setInterventionType] = useState('');
  const [variantBDescription, setVariantBDescription] = useState('Standard practice (no intervention)');

  // Category-specific state
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestion[]>([
    { id: '1', question: '', type: 'scale', required: true }
  ]);
  const [challengeConfig, setChallengeConfig] = useState<ChallengeConfig>({
    goal: '',
    metric: 'days_without_violation',
    targetValue: 7,
    durationDays: 7
  });
  const [learningContent, setLearningContent] = useState<LearningContent>({
    points: [''],
    quizQuestion: '',
    quizOptions: ['', '', '', ''],
    quizAnswer: 0
  });

  // Step 3: Target
  const [targetScope, setTargetScope] = useState<'all' | 'specific'>('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [sampleSize, setSampleSize] = useState(50);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Step 4: Duration
  const [startDate, setStartDate] = useState('');
  const [durationWeeks, setDurationWeeks] = useState(4);

  // Determine steps based on category
  const categoryNeedsSetup = INTERVENTION_TYPES.find(t => t.id === interventionType)?.needsSetup || false;
  const STEPS = categoryNeedsSetup 
    ? ['Hypothesis', 'Category', 'Setup', 'Target', 'Duration', 'Review']
    : ['Hypothesis', 'Category', 'Target', 'Duration', 'Review'];
  const totalSteps = STEPS.length;

  useEffect(() => {
    loadCompanies();
    setStartDate(new Date().toISOString().split('T')[0]);
  }, []);

  const loadCompanies = async () => {
    const { data } = await supabase
      .from('companies')
      .select('id, company_name')
      .order('company_name');
    setCompanies(data || []);
  };

  // Survey question management
  const addSurveyQuestion = () => {
    setSurveyQuestions([...surveyQuestions, {
      id: Date.now().toString(),
      question: '',
      type: 'scale',
      required: true
    }]);
  };

  const removeSurveyQuestion = (id: string) => {
    if (surveyQuestions.length > 1) {
      setSurveyQuestions(surveyQuestions.filter(q => q.id !== id));
    }
  };

  const updateSurveyQuestion = (id: string, updates: Partial<SurveyQuestion>) => {
    setSurveyQuestions(surveyQuestions.map(q => 
      q.id === id ? { ...q, ...updates } : q
    ));
  };

  // Learning points management
  const addLearningPoint = () => {
    setLearningContent({ ...learningContent, points: [...learningContent.points, ''] });
  };

  const removeLearningPoint = (index: number) => {
    if (learningContent.points.length > 1) {
      const newPoints = [...learningContent.points];
      newPoints.splice(index, 1);
      setLearningContent({ ...learningContent, points: newPoints });
    }
  };

  const updateLearningPoint = (index: number, value: string) => {
    const newPoints = [...learningContent.points];
    newPoints[index] = value;
    setLearningContent({ ...learningContent, points: newPoints });
  };

  const buildContent = (): Json => {
    if (interventionType === 'survey') {
      return { questions: surveyQuestions.filter(q => q.question.trim()) } as unknown as Json;
    } else if (interventionType === 'challenge') {
      return { challenge: challengeConfig } as unknown as Json;
    } else if (interventionType === 'micro_learning') {
      return { 
        learning: {
          points: learningContent.points.filter(p => p.trim()),
          quiz: learningContent.quizQuestion ? {
            question: learningContent.quizQuestion,
            options: learningContent.quizOptions?.filter(o => o.trim()),
            answer: learningContent.quizAnswer
          } : null
        }
      } as unknown as Json;
    }
    return {} as Json;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + (durationWeeks * 7));

      let query = supabase.from('worker_profiles').select('id');
      if (targetScope === 'specific' && selectedCompanyId) {
        query = query.eq('company_id', selectedCompanyId);
      }
      
      const { data: workers } = await query;
      const workerIds = (workers || []).map(w => w.id);
      
      const shuffled = workerIds.sort(() => Math.random() - 0.5);
      const halfIndex = Math.floor(Math.min(shuffled.length, sampleSize) / 2);
      const treatmentIds = shuffled.slice(0, halfIndex);
      const controlIds = shuffled.slice(halfIndex, halfIndex * 2);

      const content = buildContent();

      const { data, error } = await supabase
        .from('intervention_experiments')
        .insert({
          name,
          description,
          intervention_type: interventionType,
          is_active: true,
          start_date: startDate,
          end_date: end.toISOString().split('T')[0],
          company_id: targetScope === 'specific' ? selectedCompanyId : null,
          treatment_group_size: treatmentIds.length,
          control_group_size: controlIds.length,
          variant_a: {
            type: 'treatment',
            intervention_type: interventionType,
            content,
            hypothesis,
            expected_effect_size: expectedEffectSize,
          },
          variant_b: {
            type: 'control',
            description: variantBDescription,
          },
          baseline_metrics: {
            total_eligible: workerIds.length,
            sample_size: sampleSize,
            treatment_assignment: treatmentIds,
            control_assignment: controlIds,
          },
        })
        .select('id')
        .single();

      if (error) throw error;

      // Create quest templates and worker quests for treatment group
      if (data?.id && treatmentIds.length > 0) {
        const questTypeMap: Record<string, string> = {
          survey: 'survey',
          micro_learning: 'learning',
          challenge: 'challenge',
          commitment: 'commitment',
        };
        const questType = questTypeMap[interventionType] || 'learning';

        const { data: templateData, error: templateError } = await supabase
          .from('quest_templates')
          .insert({
            title: name,
            description: description || hypothesis,
            quest_type: questType,
            intervention_type: interventionType,
            xp_reward: 15,
            content,
            duration_hours: durationWeeks * 7 * 24,
            is_active: true,
          })
          .select('id')
          .single();

        if (templateError) throw templateError;

        const workerQuests = treatmentIds.map(workerId => ({
          worker_id: workerId,
          template_id: templateData.id,
          status: 'pending',
          expires_at: end.toISOString(),
          response_data: {},
        }));

        const { data: questsData, error: questsError } = await supabase
          .from('worker_quests')
          .insert(workerQuests)
          .select('id, worker_id');

        if (questsError) throw questsError;

        const interventions = (questsData || []).map(quest => ({
          company_id: targetScope === 'specific' ? selectedCompanyId : null,
          delivery_method: 'quest',
          category: interventionType,
          target_type: 'individual',
          target_worker_id: quest.worker_id,
          trigger_type: 'experiment',
          experiment_id: data.id,
          experiment_variant: 'treatment',
          title: name,
          message: description || hypothesis,
          status: 'pending',
          xp_reward: 15,
          content,
          worker_quest_id: quest.id,
        }));

        await supabase.from('interventions').insert(interventions);
      }

      toast.success(`Experiment created with ${treatmentIds.length} treatment and ${controlIds.length} control participants`);
      onComplete();
    } catch (error) {
      console.error('Error creating experiment:', error);
      toast.error('Failed to create experiment');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return name.trim() && hypothesis.trim();
    if (step === 2) return !!interventionType;
    
    if (categoryNeedsSetup) {
      if (step === 3) {
        if (interventionType === 'survey') return surveyQuestions.some(q => q.question.trim());
        if (interventionType === 'challenge') return challengeConfig.goal.trim() !== '';
        if (interventionType === 'micro_learning') return learningContent.points.some(p => p.trim());
        return true;
      }
      if (step === 4) return targetScope === 'all' || selectedCompanyId;
      if (step === 5) return startDate && durationWeeks > 0;
      return true;
    } else {
      if (step === 3) return targetScope === 'all' || selectedCompanyId;
      if (step === 4) return startDate && durationWeeks > 0;
      return true;
    }
  };

  const renderSetupStep = () => {
    if (interventionType === 'survey') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Survey Questions</h3>
            <Button variant="outline" size="sm" onClick={addSurveyQuestion}>
              <Plus className="h-4 w-4 mr-1" />
              Add Question
            </Button>
          </div>

          <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
            {surveyQuestions.map((q, index) => (
              <Card key={q.id} className="p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <GripVertical className="h-5 w-5 text-muted-foreground mt-2 shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">Q{index + 1}</span>
                      <Input
                        placeholder="Enter your question..."
                        value={q.question}
                        onChange={(e) => updateSurveyQuestion(q.id, { question: e.target.value })}
                        className="flex-1"
                      />
                      {surveyQuestions.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeSurveyQuestion(q.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Select
                        value={q.type}
                        onValueChange={(v) => updateSurveyQuestion(q.id, { type: v as SurveyQuestion['type'] })}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scale">Scale (1-10)</SelectItem>
                          <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                          <SelectItem value="text">Text</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {q.type === 'multiple_choice' && (
                      <div className="space-y-2 pl-4">
                        {(q.options || ['', '', '']).map((opt, optIdx) => (
                          <Input
                            key={optIdx}
                            placeholder={`Option ${optIdx + 1}`}
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...(q.options || ['', '', ''])];
                              newOptions[optIdx] = e.target.value;
                              updateSurveyQuestion(q.id, { options: newOptions });
                            }}
                            className="h-8"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    if (interventionType === 'challenge') {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">Challenge Configuration</h3>
          
          <div className="space-y-4">
            <div>
              <Label>Challenge Goal *</Label>
              <Input
                placeholder="e.g., Complete 7 days without safety violations"
                value={challengeConfig.goal}
                onChange={(e) => setChallengeConfig({ ...challengeConfig, goal: e.target.value })}
              />
            </div>

            <div>
              <Label>Metric to Track</Label>
              <Select
                value={challengeConfig.metric}
                onValueChange={(v) => setChallengeConfig({ ...challengeConfig, metric: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days_without_violation">Days Without Violation</SelectItem>
                  <SelectItem value="hazards_reported">Hazards Reported</SelectItem>
                  <SelectItem value="ppe_compliance">PPE Compliance Rate</SelectItem>
                  <SelectItem value="checkins_completed">Check-ins Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Value: {challengeConfig.targetValue}</Label>
                <Slider
                  value={[challengeConfig.targetValue]}
                  onValueChange={([v]) => setChallengeConfig({ ...challengeConfig, targetValue: v })}
                  min={1}
                  max={30}
                  step={1}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Duration: {challengeConfig.durationDays} days</Label>
                <Slider
                  value={[challengeConfig.durationDays]}
                  onValueChange={([v]) => setChallengeConfig({ ...challengeConfig, durationDays: v })}
                  min={1}
                  max={30}
                  step={1}
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (interventionType === 'micro_learning') {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">Learning Content</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Key Learning Points</Label>
                <Button variant="outline" size="sm" onClick={addLearningPoint}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Point
                </Button>
              </div>
              
              <div className="space-y-2">
                {learningContent.points.map((point, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                    <Input
                      placeholder="Enter learning point..."
                      value={point}
                      onChange={(e) => updateLearningPoint(index, e.target.value)}
                      className="flex-1"
                    />
                    {learningContent.points.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeLearningPoint(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Card className="p-4 bg-muted/30">
              <Label className="text-sm font-medium">Optional Quiz Question</Label>
              <p className="text-xs text-muted-foreground mb-3">Add a quiz to test comprehension</p>
              
              <div className="space-y-3">
                <Input
                  placeholder="Quiz question..."
                  value={learningContent.quizQuestion}
                  onChange={(e) => setLearningContent({ ...learningContent, quizQuestion: e.target.value })}
                />
                
                {learningContent.quizQuestion && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {learningContent.quizOptions?.map((opt, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="quiz-answer"
                            checked={learningContent.quizAnswer === index}
                            onChange={() => setLearningContent({ ...learningContent, quizAnswer: index })}
                            className="accent-primary"
                          />
                          <Input
                            placeholder={`Option ${index + 1}`}
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...(learningContent.quizOptions || [])];
                              newOptions[index] = e.target.value;
                              setLearningContent({ ...learningContent, quizOptions: newOptions });
                            }}
                            className="flex-1 h-8"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Select the correct answer</p>
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderStep = () => {
    if (step === 1) {
      return (
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Experiment Name *</Label>
            <Input
              id="name"
              placeholder="e.g., PPE Reminder Effectiveness Study"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the experiment..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="hypothesis">Hypothesis *</Label>
            <Textarea
              id="hypothesis"
              placeholder="e.g., Workers who receive daily micro-learning nudges will have 20% fewer safety violations..."
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              rows={3}
            />
          </div>
          <div>
            <Label>Expected Effect Size: {expectedEffectSize.toFixed(2)}</Label>
            <p className="text-xs text-muted-foreground mb-2">
              0.2 = small, 0.5 = medium, 0.8 = large
            </p>
            <Slider
              value={[expectedEffectSize]}
              onValueChange={(v) => setExpectedEffectSize(v[0])}
              min={0.1}
              max={1.0}
              step={0.05}
            />
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-4">
          <div>
            <Label>Intervention Type *</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {INTERVENTION_TYPES.map((type) => (
                <Card
                  key={type.id}
                  className={cn(
                    "p-4 cursor-pointer transition-all",
                    interventionType === type.id 
                      ? "ring-2 ring-primary bg-primary/5" 
                      : "hover:bg-muted/50"
                  )}
                  onClick={() => setInterventionType(type.id)}
                >
                  <p className="font-medium text-sm">{type.label}</p>
                  <p className="text-xs text-muted-foreground">{type.description}</p>
                </Card>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="variantB">Control Group (Variant B)</Label>
            <Textarea
              id="variantB"
              placeholder="What does the control group receive? (default: nothing)"
              value={variantBDescription}
              onChange={(e) => setVariantBDescription(e.target.value)}
              rows={2}
            />
          </div>
        </div>
      );
    }

    if (categoryNeedsSetup) {
      if (step === 3) return renderSetupStep();
      if (step === 4) return renderTargetStep();
      if (step === 5) return renderDurationStep();
      if (step === 6) return renderReviewStep();
    } else {
      if (step === 3) return renderTargetStep();
      if (step === 4) return renderDurationStep();
      if (step === 5) return renderReviewStep();
    }

    return null;
  };

  const renderTargetStep = () => (
    <div className="space-y-4">
      <div>
        <Label>Target Scope</Label>
        <RadioGroup value={targetScope} onValueChange={(v) => setTargetScope(v as 'all' | 'specific')}>
          <div className="flex items-center space-x-2 mt-2">
            <RadioGroupItem value="all" id="all" />
            <Label htmlFor="all" className="flex items-center gap-2 cursor-pointer">
              <Building2 className="h-4 w-4" />
              All Companies (Cross-company study)
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="specific" id="specific" />
            <Label htmlFor="specific" className="flex items-center gap-2 cursor-pointer">
              <Target className="h-4 w-4" />
              Specific Company
            </Label>
          </div>
        </RadioGroup>
      </div>

      {targetScope === 'specific' && (
        <div>
          <Label>Select Company</Label>
          <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a company..." />
            </SelectTrigger>
            <SelectContent>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>Sample Size (per group): {Math.floor(sampleSize / 2)}</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Total participants: {sampleSize} (split 50/50 into treatment and control)
        </p>
        <Slider
          value={[sampleSize]}
          onValueChange={(v) => setSampleSize(v[0])}
          min={10}
          max={200}
          step={10}
        />
      </div>
    </div>
  );

  const renderDurationStep = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="startDate">Start Date</Label>
        <Input
          id="startDate"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>
      <div>
        <Label>Duration: {durationWeeks} weeks</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Recommended: 4-8 weeks for behavior change studies
        </p>
        <Slider
          value={[durationWeeks]}
          onValueChange={(v) => setDurationWeeks(v[0])}
          min={1}
          max={12}
          step={1}
        />
      </div>
      <Card className="p-4 bg-secondary/50">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4" />
          <span>
            {startDate && (
              <>
                Experiment will run from {new Date(startDate).toLocaleDateString()} to{' '}
                {new Date(new Date(startDate).getTime() + durationWeeks * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}
              </>
            )}
          </span>
        </div>
      </Card>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <CheckCircle className="h-5 w-5 text-green-500" />
        Review Experiment Setup
      </h3>
      
      <Card className="p-4 space-y-3">
        <div>
          <p className="text-xs text-muted-foreground">Experiment Name</p>
          <p className="font-medium">{name}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Hypothesis</p>
          <p className="text-sm">{hypothesis}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Intervention Type</p>
            <p className="font-medium">{INTERVENTION_TYPES.find(t => t.id === interventionType)?.label}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Expected Effect</p>
            <p className="font-medium">{expectedEffectSize.toFixed(2)}</p>
          </div>
        </div>
        {interventionType === 'survey' && (
          <div>
            <p className="text-xs text-muted-foreground">Survey Questions</p>
            <p className="font-medium">{surveyQuestions.filter(q => q.question.trim()).length} questions</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Target Scope</p>
            <p className="font-medium">
              {targetScope === 'all' 
                ? 'All Companies' 
                : companies.find(c => c.id === selectedCompanyId)?.company_name || 'Unknown'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Sample Size</p>
            <p className="font-medium">{sampleSize} total ({Math.floor(sampleSize/2)} per group)</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Duration</p>
          <p className="font-medium">{durationWeeks} weeks</p>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-1">
        {STEPS.map((label, index) => (
          <div key={label} className="flex items-center">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
              index + 1 <= step 
                ? "bg-primary text-primary-foreground" 
                : "bg-secondary text-muted-foreground"
            )}>
              {index + 1}
            </div>
            {index < STEPS.length - 1 && (
              <div className={cn(
                "w-6 h-0.5 mx-1",
                index + 1 < step ? "bg-primary" : "bg-secondary"
              )} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-4 text-xs text-muted-foreground">
        {STEPS.map((label) => (
          <span key={label} className="w-14 text-center">{label}</span>
        ))}
      </div>

      {/* Step content */}
      <div className="min-h-[350px]">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="ghost" onClick={step === 1 ? onCancel : () => setStep(step - 1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        
        {step < totalSteps ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FlaskConical className="h-4 w-4 mr-2" />
                Launch Experiment
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
